import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { trackSignUp } from "@/lib/gtm";
import { trackCompleteRegistration } from "@/lib/metaPixel";
import { useAuth, dashboardFor } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Compass, CheckCircle2, Users, Store, CalendarPlus, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { TERMS_VERSION, PRIVACY_VERSION } from "@/pages/Auth";

const ROLE_TILES = [
  { value: "parent", label: "Parent", icon: Users, desc: "Find events & vendors" },
  { value: "vendor", label: "Vendor", icon: Store, desc: "Offer services" },
  { value: "event_creator", label: "Event Creator", icon: CalendarPlus, desc: "Host events" },
  { value: "marketplace_user", label: "Marketplace User", icon: ShoppingBag, desc: "Sell products" },
];

export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);
  // Multi-role sign-in picker (existing accounts)
  const [rolePick, setRolePick] = useState(null);
  const [selectedRole, setSelectedRole] = useState("");
  // New Google sign-up — must mirror email registration (role picker + consents)
  const [newRegistration, setNewRegistration] = useState(null); // { email, name, picture, nonce }
  const [registerRole, setRegisterRole] = useState("parent");
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const sessionId = location.hash.split("session_id=")[1]?.split("&")[0];
    if (!sessionId) {
      navigate("/login");
      return;
    }
    // Pull the role the user picked on the sign-up page (if any) so the
    // backend can route them to the add-role flow when the Google account
    // exists but doesn't yet have that role.
    let desiredRole = "";
    try {
      desiredRole = sessionStorage.getItem("cck_google_desired_role_v1") || "";
      sessionStorage.removeItem("cck_google_desired_role_v1");
    } catch { /* storage disabled */ }
    (async () => {
      try {
        const headers = { "X-Session-ID": sessionId };
        if (desiredRole) headers["X-Desired-Role"] = desiredRole;
        const { data } = await api.post("/auth/google/session", null, { headers });
        window.history.replaceState(null, "", window.location.pathname);
        if (data && data.needs_registration) {
          setNewRegistration({ email: data.email, name: data.name, picture: data.picture, nonce: data.nonce,
                               desired_role: data.desired_role || "", existing_roles: data.existing_roles || [] });
          if (data.desired_role) setRegisterRole(data.desired_role);
          return;
        }
        if (data && data.multiple_roles) {
          setRolePick({ roles: data.roles || [], nonce: data.nonce, email: data.email });
          setSelectedRole((data.roles && data.roles[0]?.role) || "");
          return;
        }
        setUser(data);
        navigate(dashboardFor(data), { replace: true, state: { user: data } });
      } catch {
        navigate("/login", { replace: true });
      }
    })();
  }, [location, navigate, setUser]);

  const submitRole = async (e) => {
    e.preventDefault();
    if (!selectedRole || !rolePick?.nonce) return;
    setLoading(true);
    try {
      const { data } = await api.post("/auth/google/select-role", { nonce: rolePick.nonce, role: selectedRole });
      setUser(data);
      toast.success(`Welcome back, ${data.name}!`);
      navigate(dashboardFor(data), { replace: true, state: { user: data } });
    } catch (err) {
      toast.error(errMsg(err, "Sign in failed"));
      navigate("/login", { replace: true });
    } finally {
      setLoading(false);
    }
  };

  const submitNewRegistration = async (e) => {
    e.preventDefault();
    if (!newRegistration?.nonce || !registerRole || !agreeTerms || !agreePrivacy) return;
    setLoading(true);
    try {
      const { data } = await api.post("/auth/google/complete-registration", {
        nonce: newRegistration.nonce,
        role: registerRole,
        terms_version: TERMS_VERSION,
        privacy_version: PRIVACY_VERSION,
      });
      trackSignUp(registerRole, "google");
      trackCompleteRegistration(registerRole);
      setUser(data);
      toast.success(`Welcome to Compass Kids, ${data.name}!`);
      navigate(dashboardFor(data), { replace: true, state: { user: data } });
    } catch (err) {
      toast.error(errMsg(err, "Could not complete sign up"));
    } finally {
      setLoading(false);
    }
  };

  if (newRegistration) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream px-4 py-12 grain" data-testid="google-register-page">
        <div className="bg-white/80 border border-sage/20 rounded-3xl p-8 sm:p-10 max-w-md w-full shadow-sm fade-in">
          <div className="flex items-center gap-2 mb-6">
            <span className="w-10 h-10 rounded-full bg-sage flex items-center justify-center">
              <Compass className="w-5 h-5 text-white" />
            </span>
            <div>
              <h1 className="text-xl font-black text-forest leading-tight">Complete your sign up</h1>
              <p className="text-xs text-forest/60">{newRegistration.email}</p>
            </div>
          </div>
          <p className="text-sm text-forest/70 mb-4">
            Welcome{newRegistration.name ? `, ${newRegistration.name.split(" ")[0]}` : ""}!{" "}
            {newRegistration.existing_roles && newRegistration.existing_roles.length > 0
              ? "This Google account already exists on Compass Kids — you're adding a new role to it. Confirm the role and agree to our terms to continue."
              : "Pick the role you're joining as and agree to our terms to finish creating your Compass Kids account."}
          </p>

          <form onSubmit={submitNewRegistration} className="space-y-4">
            <div className="grid grid-cols-2 gap-3" data-testid="google-register-role-picker">
              {ROLE_TILES.map((r) => (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => setRegisterRole(r.value)}
                  className={`rounded-2xl border-2 p-4 text-left transition-colors ${
                    registerRole === r.value ? "border-sage bg-sage-50" : "border-sage/20 bg-white hover:border-sage/50"
                  }`}
                  data-testid={`google-register-role-${r.value}-btn`}
                >
                  <r.icon className={`w-5 h-5 mb-2 ${registerRole === r.value ? "text-sage-dark" : "text-forest/50"}`} />
                  <div className="font-heading font-bold text-sm text-forest flex items-center gap-1">
                    {r.label}
                    {registerRole === r.value && <CheckCircle2 className="w-4 h-4 text-sage-dark" />}
                  </div>
                  <div className="text-xs text-forest/60">{r.desc}</div>
                </button>
              ))}
            </div>

            <div className="space-y-2 rounded-xl bg-softstone/50 border border-sage/20 p-3" data-testid="google-register-consent-block">
              <label className="flex items-start gap-2 text-xs text-forest/80 cursor-pointer" data-testid="google-register-agree-terms-label">
                <input
                  type="checkbox"
                  checked={agreeTerms}
                  onChange={(e) => setAgreeTerms(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-sage-dark cursor-pointer"
                  data-testid="google-register-agree-terms-checkbox"
                />
                <span>
                  I agree to the{" "}
                  <Link to="/terms" target="_blank" rel="noopener noreferrer" className="font-bold text-terracotta-dark hover:underline" data-testid="google-register-terms-link">
                    Terms &amp; Conditions
                  </Link>
                  .
                </span>
              </label>
              <label className="flex items-start gap-2 text-xs text-forest/80 cursor-pointer" data-testid="google-register-agree-privacy-label">
                <input
                  type="checkbox"
                  checked={agreePrivacy}
                  onChange={(e) => setAgreePrivacy(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-sage-dark cursor-pointer"
                  data-testid="google-register-agree-privacy-checkbox"
                />
                <span>
                  I agree to the{" "}
                  <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="font-bold text-terracotta-dark hover:underline" data-testid="google-register-privacy-link">
                    Privacy Policy
                  </Link>
                  .
                </span>
              </label>
            </div>

            <Button
              type="submit"
              disabled={loading || !registerRole || !agreeTerms || !agreePrivacy}
              className="w-full rounded-full bg-sage hover:bg-sage-dark text-white shadow-md"
              data-testid="google-register-submit-btn"
            >
              {loading ? "Creating your account..." : "Complete sign up"}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  if (rolePick) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream px-4 py-12 grain" data-testid="google-role-picker">
        <div className="bg-white/80 border border-sage/20 rounded-3xl p-8 sm:p-10 max-w-md w-full shadow-sm fade-in">
          <div className="flex items-center gap-2 mb-6">
            <span className="w-10 h-10 rounded-full bg-sage flex items-center justify-center">
              <Compass className="w-5 h-5 text-white" />
            </span>
            <div>
              <h1 className="text-xl font-black text-forest leading-tight">Choose a role to sign in as</h1>
              <p className="text-xs text-forest/60">{rolePick.email}</p>
            </div>
          </div>
          <p className="text-sm text-forest/70 mb-4">This Google account is linked to more than one Compass Kids profile. Pick which one you want to open.</p>
          <form onSubmit={submitRole} className="space-y-3">
            {rolePick.roles.map((r) => (
              <label
                key={r.role}
                className={`block rounded-2xl border-2 p-4 cursor-pointer transition-colors ${selectedRole === r.role ? "border-sage bg-sage-50" : "border-sage/20 bg-white hover:border-sage/50"}`}
                data-testid={`google-role-option-${r.role}`}
              >
                <input
                  type="radio"
                  name="googleRole"
                  value={r.role}
                  checked={selectedRole === r.role}
                  onChange={() => setSelectedRole(r.role)}
                  className="sr-only"
                  data-testid={`google-role-radio-${r.role}`}
                />
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-heading font-bold text-forest">{r.label}</div>
                    <div className="text-xs text-forest/60">Sign in to your {r.label} dashboard</div>
                  </div>
                  {selectedRole === r.role && <CheckCircle2 className="w-5 h-5 text-sage-dark" />}
                </div>
              </label>
            ))}
            <Button type="submit" disabled={loading || !selectedRole} className="w-full rounded-full bg-sage hover:bg-sage-dark text-white shadow-md" data-testid="google-role-continue-btn">
              {loading ? "Signing you in..." : "Continue"}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-cream gap-4" data-testid="auth-callback">
      <Compass className="w-10 h-10 text-sage animate-spin" style={{ animationDuration: "2s" }} />
      <p className="text-forest/60 text-sm font-semibold">Signing you in...</p>
    </div>
  );
}
